import { useEffect, useRef, useState, useCallback } from "react";
import ekonlah from "../../../assets/readdy/ekonlah.jpg";

const testimonials = [
  {
    name: "Vincent Ekonlah",
    role: "Founder & CEO, Ekonlah Enterprise",
    avatar: ekonlah,
    quote: "Mesimo Inc. delivered our webiste ahead of schedule and beyond our expectations. The team communicated clearly throughout the whole developmental phases.",
    rating: 5,
    tag: "Website",
  }
];

export default function Testimonials() {
  const [visible, setVisible] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const autoRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startAuto = useCallback(() => {
    if (autoRef.current) clearInterval(autoRef.current);
    autoRef.current = setInterval(() => {
      setCurrentIdx((prev) => (prev + 1) % testimonials.length);
    }, 4500);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    startAuto();
    return () => { if (autoRef.current) clearInterval(autoRef.current); };
  }, [startAuto]);

  const goTo = (i: number) => {
    setCurrentIdx(i);
    startAuto();
  };

  const t = testimonials[currentIdx];

  return (
    <section id="testimonials" ref={sectionRef} className="py-24 md:py-36 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`mb-16 text-center transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4 block">Testimonials</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-950 leading-tight">
            What Our<br />
            <span className="italic font-light text-gray-400">Clients Say</span>
          </h2>
        </div>

        {/* Main Testimonial */}
        <div className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="relative max-w-3xl mx-auto bg-gray-50 rounded-3xl p-10 md:p-14">
            <div className="w-12 h-12 flex items-center justify-center bg-gray-950 rounded-full mb-8">
              <i className="ri-double-quotes-l text-white text-xl"></i>
            </div>

            <div className="min-h-[100px] mb-8">
              <p key={currentIdx} className="text-gray-700 text-lg md:text-xl leading-relaxed font-medium animate-[fadeIn_0.4s_ease]">
                &ldquo;{t.quote}&rdquo;
              </p>
            </div>

            <div className="flex items-center gap-1 mb-6">
              {Array.from({ length: t.rating }).map((_, i) => (
                <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
              ))}
            </div>

            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full overflow-hidden flex-shrink-0">
                  <img src={t.avatar} alt={t.name} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <div className="font-bold text-gray-900">{t.name}</div>
                  <div className="text-gray-500 text-sm">{t.role}</div>
                </div>
              </div>
              <span className="text-xs font-medium bg-gray-200 text-gray-600 px-3 py-1.5 rounded-full whitespace-nowrap">{t.tag}</span>
            </div>

            <div className="absolute top-6 right-8 text-gray-200 text-6xl font-serif leading-none select-none">"</div>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2.5 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`rounded-full transition-all duration-300 cursor-pointer ${
                  currentIdx === i ? "w-6 h-2.5 bg-gray-950" : "w-2.5 h-2.5 bg-gray-200 hover:bg-gray-300"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Mini cards row */}
        <div className={`mt-16 grid grid-cols-2 md:grid-cols-5 gap-4 transition-all duration-700 delay-400 ${visible ? "opacity-100" : "opacity-0"}`}>
          {testimonials.map((item, i) => (
            <button
              key={item.name}
              onClick={() => goTo(i)}
              className={`text-left p-4 rounded-2xl border transition-all cursor-pointer ${
                currentIdx === i ? "border-gray-950 bg-gray-950 text-white" : "border-gray-100 bg-white hover:border-gray-200"
              }`}
            >
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                  <img src={item.avatar} alt={item.name} className="w-full h-full object-cover object-top" />
                </div>
                <span className={`font-semibold text-xs truncate ${currentIdx === i ? "text-white" : "text-gray-800"}`}>{item.name}</span>
              </div>
              <p className={`text-xs leading-relaxed line-clamp-2 ${currentIdx === i ? "text-white/60" : "text-gray-400"}`}>{item.role}</p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
